import React, { createContext, ReactNode, useContext } from 'react';
import type {
  captureException,
  captureMessage,
  setContext,
  setTag,
  setTags,
  withScope
} from '@sentry/core';
import type { Extra, Extras } from '@sentry/types';

export type LogErrorFn = (
  err: Parameters<typeof captureException>[0],
  extra?: Extras | Extra
) => void;
export type LogMessageFn = typeof captureMessage;
export type SetTagFn = typeof setTag;
export type SetTagsFn = typeof setTags;
export type WithScopeFn = typeof withScope;
export type SetContextFn = typeof setContext;

export interface ErrorLoggingContextValue {
  logError: LogErrorFn;
  logMessage: LogMessageFn;
  setTag: SetTagFn;
  setTags: SetTagsFn;
  withScope: WithScopeFn;
  setContext: SetContextFn;
}

export const ErrorLoggingContext =
  createContext<ErrorLoggingContextValue | null>(null);

if (process.env.NODE_ENV === 'development') {
  ErrorLoggingContext.displayName = 'ErrorLoggingContext';
}

export const useStravaErrorLogging = () => {
  const context = useContext(ErrorLoggingContext);

  if (!context) {
    throw new Error(
      'useStravaErrorLogging must be used within the ErrorLoggingProvider'
    );
  }

  return context;
};

interface ErrorLoggingProviderProps extends ErrorLoggingContextValue {
  children: ReactNode;
}

export const ErrorLoggingProvider = ({
  children,
  ...errorLoggingContextValue
}: ErrorLoggingProviderProps) => {
  return (
    <ErrorLoggingContext.Provider
      value={{
        ...errorLoggingContextValue
      }}
    >
      {children}
    </ErrorLoggingContext.Provider>
  );
};

if (process.env.NODE_ENV === 'development') {
  ErrorLoggingProvider.displayName = 'ErrorLoggingProvider';
}
